import React from 'react';
import b_ from 'b_';

import Block from 'client/common/components/block';
import {RadioButton} from 'client/common/components/lego';
import AudienceSettings from 'client/common/components/audience-settings';

import {AudienceTab} from 'client/common/components/survey-settings/types';
import {AudienceType, SurveyScope, SurveysGoal, SurveysParam} from 'client/common/types';
import {AudienceTargeting} from 'client/common/utils/audience';
import {
    useAudienceRequest,
    useCanRequestAudience,
    useRequestAudienceTabs,
    useUpdateAudienceRequest
} from 'client/common/hooks/audience';
import {useUpdateSurveySettings} from 'client/common/hooks/survey';
import {useIsSuperuser} from 'client/common/hooks/user';

import EditSettingsModal from 'client/common/components/survey-settings/modals/edit-settings';

import {useSurveySlug} from 'client/common/contexts/slug';

import I18N from 'assets/i18n/brandlift-project';
import {useCanEditAudience} from 'client/common/hooks/edit-mode';
import {sendGoal, sendParams} from 'client/common/utils/metrika';

import WarningHint from '../warning-hint';

import './audience-block.css';

const b = b_.with('audience-block');

const i18n = I18N.surveyEditor;

const audienceTypeToTab: {[key in AudienceType]?: AudienceTab} = {
    NONE: AudienceTab.Own,
    MANUAL: AudienceTab.Manual
};

const tabsTranslates: {[key in AudienceTab]: string} = {
    [AudienceTab.Request]: i18n.tabs.selectorRequest,
    [AudienceTab.Own]: i18n.tabs.selectorOwn,
    [AudienceTab.Manual]: i18n.tabs.manual
};

const getTabOptions = (tabs: Array<AudienceTab>) => {
    return tabs.map(tab => {
        return {
            value: tab,
            children: tabsTranslates[tab]
        };
    });
};

// eslint-disable-next-line max-statements
export default function AudienceBlock() {
    const slug = useSurveySlug();
    const isSuperUser = useIsSuperuser();

    const onRequestUpdate = useUpdateAudienceRequest(slug);
    const canRequest = useCanRequestAudience(slug);
    const {data} = useAudienceRequest(slug);
    const updateSettings = useUpdateSurveySettings(slug);

    const tabs = useRequestAudienceTabs(data?.type);

    const tabOptions = getTabOptions(tabs);

    const selectedTab = (data && audienceTypeToTab[data.type]) ?? AudienceTab.Request;

    const handleAudienceChange = (value: AudienceTab) => {
        if (value === AudienceTab.Request) {
            if (!canRequest) {
                return;
            }

            return onRequestUpdate({
                type: AudienceTargeting.DEFAULT_AUDIENCE.type,
                categories: AudienceTargeting.DEFAULT_CATEGORIES,
                interests: null,
                quotas: null
            });
        }

        if (value === AudienceTab.Own) {
            sendGoal(SurveysGoal.ChangeAudienceType);
            sendParams([SurveysParam.SelectedAudienceType, AudienceTab.Own]);

            return onRequestUpdate({
                type: AudienceType.None,
                interests: null,
                quotas: null
            });
        }

        if (value === AudienceTab.Manual) {
            void updateSettings({
                scope: [SurveyScope.External]
            });

            return onRequestUpdate({
                type: AudienceType.Manual,
                interests: null,
                quotas: null
            });
        }
    };

    const canEdit = useCanEditAudience();

    return (
        <Block
            className={b()}
            title={i18n.audienceBlock.title}
            description={i18n.audienceBlock.description}
        >
            <RadioButton
                className={b('selector')}
                onChange={e => handleAudienceChange(e.target.value as AudienceTab)}
                options={tabOptions}
                size='m'
                view='dl'
                value={selectedTab}
                disabled={!canEdit}
            />
            {selectedTab === AudienceTab.Manual && isSuperUser && (
                <WarningHint text={i18n.audienceBlock.manualWarning} />
            )}
            {selectedTab !== AudienceTab.Own && <AudienceSettings tab={selectedTab} />}
            {selectedTab === AudienceTab.Own && (
                <EditSettingsModal onClose={() => null} slug={slug} />
            )}
        </Block>
    );
}
